export type ToastType = 'error' | 'warning' | 'info' | 'success';
import './toast.scss';

interface ToastOptions {
    message: string;
    type?: ToastType;
    duration?: number;
}

const CONTAINER_ID = 'map-toast-container';
const BATCH_DELAY_MS = 1000;

let pendingLayerErrors: string[] = [];
let batchTimeout: ReturnType<typeof setTimeout> | null = null;
let hasShownToast = false;

function getOrCreateContainer(): HTMLElement | null {
    let container = document.getElementById(CONTAINER_ID);
    if (!container) {
        // Find the map container - it has class "map-element"
        const mapContainer = document.querySelector('.map-element');
        if (!mapContainer) {
            console.warn('Map container not found for toast');
            return null;
        }

        container = document.createElement('div');
        container.id = CONTAINER_ID;
        container.className = 'toast-container position-absolute bottom-0 start-50 translate-middle-x p-3';

        mapContainer.appendChild(container);
    }

    return container;
}

export function showToast({ message, duration = 5000 }: ToastOptions): void {
    if (hasShownToast) return;
    const container = getOrCreateContainer();
    if (!container) return;

    const toastEl = document.createElement('div');
    toastEl.className = `toast show align-items-center`;
    toastEl.setAttribute('role', 'alert');

    const toastBody = document.createElement('div');
    toastBody.className = `toast-body px-2 py-1`;
    toastBody.textContent = message;
    toastEl.appendChild(toastBody);

    container.appendChild(toastEl);
    hasShownToast = true;

    setTimeout(() => {
        toastEl.remove();
    }, duration);
}

export function showLayerErrorToast(layerName: string): void {
    // don't show toast on new API call if toast has been closed earlier
    if (hasShownToast) return;

    // Don't show toast on narrow screens where the mobile map overlay is visible
    const mobileOverlay = document.querySelector('.mobile-link-navigation');
    if (mobileOverlay && (mobileOverlay as HTMLElement).offsetParent !== null) return;

    // Don't add duplicates
    if (!pendingLayerErrors.includes(layerName)) {
        pendingLayerErrors.push(layerName);
    }

    // Clear existing timeout and set a new one: toast fires after batchTimeout period of silence
    if (batchTimeout) {
        clearTimeout(batchTimeout);
    }

    batchTimeout = setTimeout(() => {
        if (pendingLayerErrors.length === 0) return;

        const layerList = pendingLayerErrors.join(', ');
        const message = pendingLayerErrors.length === 1 ? `Kunde inte ladda ${layerList}` : `Kunde inte ladda: ${layerList}`;

        showToast({ message });

        // Reset
        pendingLayerErrors = [];
        batchTimeout = null;
    }, BATCH_DELAY_MS);
}

export function getLayerDisplayName(customName?: string, sourceId?: string): string {
    // Priority: custom name > layerDisplayNames > fallback
    // Custom names are set (as displayName) in defaultOptions for each Map Source, where applicable. E.g. TrafficCameraSource.ts. Names where more layers come from the same source are given from const layerDisplayNames below, and otherwise its just "kartdata"
    // displayNames of tiled layers (cykelvägar) are set in respective map LayerFactory

    return customName || layerDisplayNames[sourceId] || 'kartdata';
}

const layerDisplayNames: Record<string, string> = {
    // Parkings section GBG
    'shuttle-parking-source': 'Pendelparkeringar',
    'parking-gbg-PublicTimeParkings': 'Tidsbegränsade parkeringar',
    'parking-gbg-PublicTollParkings': 'Avgiftsbelagda parkeringar',
    'parking-gbg-PrivateTollParkings': 'Avgiftsbelagda parkeringar',
    'parking-gbg-BusParkings': 'Bussparkeringar',
    'parking-gbg-TruckParkings': 'Lastbilsparkeringar',
    'parking-gbg-HandicapParkings': 'Parkeringar för rörelsehindrade',
    'parking-gbg-MCParkings': 'Motorcykelparkeringar',
    'parking-gbg-BikeParkings': 'Cykelparkeringar',

    // Parkings section VST
    'parking-sth-pbuss': 'Bussparkeringar',
    'parking-sth-plastbil': 'Lastbilsparkeringar',
    'parking-sth-prorelsehindrad': 'Parkeringar för rörelsehindrade',
    'parking-sth-pmotorcykel': 'Motorcykelparkeringar',
};
